import { z } from "zod";
import { MAXIMUM_REPORT_DURATION_SECONDS, MAXIMUM_REPORTING_RANGE_DAYS, } from "./reporting-constants.js";
import { MAXIMUM_SUBGROUP_REVISION, MAXIMUM_SUBGROUPS_PER_BRAND, MAXIMUM_SUBGROUP_VALUES, SUBGROUP_UNASSIGNED_LABEL, SUBGROUP_UNASSIGNED_VALUE, } from "./subgroup-constants.js";
export const REPORT_FILTER_STATE_VERSION = 1;
export const SAVED_VIEW_STORAGE_VERSION = 1;
/** Leaves room for the request target and headers within an 8 KiB proxy buffer. */
export const MAXIMUM_REPORT_FILTER_URL_LENGTH = 6_000;
export const MAXIMUM_REPORT_FILTER_BRANDS = 100;
export const MAXIMUM_REPORT_FILTER_ACCOUNTS = 500;
export const MAXIMUM_FILTER_CATALOG_ACCOUNTS = 10_000;
export const MAXIMUM_FILTER_CATALOG_SUBGROUP_VALUES = 10_000;
export const MAXIMUM_REPORT_FILTER_GROUP_SELECTIONS = 100;
export const MAXIMUM_REPORT_FILTER_VALUES_PER_GROUP = MAXIMUM_SUBGROUP_VALUES;
export const MAXIMUM_REPORT_FILTER_OPTIONS = 100;
export const MAXIMUM_SAVED_VIEW_NAME_LENGTH = 200;
/** Keeps the non-paginated accessible saved-view catalog bounded. */
export const MAXIMUM_ACCESSIBLE_SAVED_VIEWS = 20;
/** Bounds one view's explicit ACL; peopleCount can therefore be at most 21. */
export const MAXIMUM_SAVED_VIEW_COLLABORATORS = 20;
/** Backward-compatible name for the accessible catalog response ceiling. */
export const MAXIMUM_SAVED_VIEWS_PER_USER = MAXIMUM_ACCESSIBLE_SAVED_VIEWS;
/** Together with the per-user count cap, bounds a list response below 8 MiB. */
export const MAXIMUM_SAVED_VIEW_STORAGE_BYTES = 384 * 1_024;
export const reportPlayStatuses = [
    "hosted",
    "not_tracked",
    "pending",
    "unavailable",
];
const reportDatePresetDays = new Map([
    ["7", 7],
    ["30", 30],
    ["90", 90],
]);
export const canonicalUuidSchema = z
    .string()
    .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/, "Expected a canonical UUID");
const boundedFlatValue = z
    .string()
    .trim()
    .min(1)
    .max(255)
    .refine((value) => !value.includes(","), "Comma is reserved as a list delimiter");
const displayName = z.string().trim().min(1).max(255);
const subgroupDisplayName = z.string().trim().min(1).max(200);
const realIsoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .refine((value) => {
    if (value.startsWith("0000-"))
        return false;
    const parsed = new Date(`${value}T00:00:00.000Z`);
    return (!Number.isNaN(parsed.valueOf()) &&
        parsed.toISOString().slice(0, 10) === value);
}, "Expected a real ISO date");
const nullableIsoDate = realIsoDate.nullable().default(null);
function compareCodePoints(left, right) {
    return left < right ? -1 : left > right ? 1 : 0;
}
function uniqueSortedStrings(item, maximum, message, minimum = 0) {
    return z
        .array(item)
        .min(minimum)
        .max(maximum)
        .superRefine((values, context) => {
        if (new Set(values).size !== values.length) {
            context.addIssue({ code: "custom", message });
        }
    })
        .transform((values) => [...values].sort(compareCodePoints));
}
const reportBrandIdsSchema = uniqueSortedStrings(canonicalUuidSchema, MAXIMUM_REPORT_FILTER_BRANDS, "Brand identifiers must be unique");
const reportAccountIdsSchema = uniqueSortedStrings(boundedFlatValue, MAXIMUM_REPORT_FILTER_ACCOUNTS, "Account identifiers must be unique", 1).nullable();
const reportOptionValuesSchema = (message) => uniqueSortedStrings(boundedFlatValue, MAXIMUM_REPORT_FILTER_OPTIONS, message);
const presetDateSchema = z
    .object({ preset: z.union([z.literal(7), z.literal(30), z.literal(90)]) })
    .strict();
const customDateSchema = z
    .object({
    from: realIsoDate,
    preset: z.literal("custom"),
    to: realIsoDate,
})
    .strict();
export const reportFilterDateV1Schema = z
    .discriminatedUnion("preset", [presetDateSchema, customDateSchema])
    .superRefine((date, context) => {
    if (date.preset === "custom") {
        if (date.from > date.to) {
            context.addIssue({
                code: "custom",
                message: "Custom date range must not run backwards",
            });
            return;
        }
        const days = (Date.parse(`${date.to}T00:00:00.000Z`) -
            Date.parse(`${date.from}T00:00:00.000Z`)) /
            86_400_000 +
            1;
        if (!Number.isSafeInteger(days) ||
            days < 1 ||
            days > MAXIMUM_REPORTING_RANGE_DAYS) {
            context.addIssue({
                code: "custom",
                message: `Custom date range cannot exceed ${MAXIMUM_REPORTING_RANGE_DAYS} days`,
            });
        }
    }
});
export const reportGroupSelectionV1Schema = z
    .object({
    brandId: canonicalUuidSchema,
    groupId: canonicalUuidSchema,
    includeUnassigned: z.boolean(),
    valueIds: uniqueSortedStrings(canonicalUuidSchema, MAXIMUM_REPORT_FILTER_VALUES_PER_GROUP, "Subgroup value identifiers must be unique"),
})
    .strict()
    .refine(({ includeUnassigned, valueIds }) => includeUnassigned || valueIds.length > 0, "At least one subgroup value or the unassigned bucket is required");
const reportGroupSelectionsV1Schema = z
    .array(reportGroupSelectionV1Schema)
    .max(MAXIMUM_REPORT_FILTER_GROUP_SELECTIONS)
    .superRefine((selections, context) => {
    const keys = selections.map(({ brandId, groupId }) => `${brandId}\0${groupId}`);
    if (new Set(keys).size !== keys.length) {
        context.addIssue({
            code: "custom",
            message: "Subgroup selections must be unique by brand and group",
        });
    }
})
    .transform((selections) => [...selections].sort((left, right) => compareCodePoints(`${left.brandId}\0${left.groupId}`, `${right.brandId}\0${right.groupId}`)));
const nullableDuration = z
    .number()
    .finite()
    .min(0)
    .max(MAXIMUM_REPORT_DURATION_SECONDS)
    .nullable();
const nullableMetricRangeBound = z.number().finite().min(0).nullable().default(null);
function addOrderedRangeIssue(context, minimum, maximum, message) {
    if (minimum != null && maximum != null && minimum > maximum) {
        context.addIssue({ code: "custom", message });
    }
}
export const reportFilterStateV1Schema = z
    .object({
    accountIds: reportAccountIdsSchema,
    adNameSearch: z.string().trim().min(1).max(256).nullable(),
    brandIds: reportBrandIdsSchema,
    date: reportFilterDateV1Schema,
    durationMax: nullableDuration,
    durationMin: nullableDuration,
    firstSeenFrom: nullableIsoDate,
    firstSeenTo: nullableIsoDate,
    groupSelections: reportGroupSelectionsV1Schema,
    objectives: reportOptionValuesSchema("Objectives must be unique"),
    optimizationGoals: reportOptionValuesSchema("Optimization goals must be unique"),
    playStatuses: uniqueSortedStrings(z.enum(reportPlayStatuses), reportPlayStatuses.length, "Play statuses must be unique"),
    roasMax: nullableMetricRangeBound,
    roasMin: nullableMetricRangeBound,
    spendMax: nullableMetricRangeBound,
    spendMin: nullableMetricRangeBound,
    version: z.literal(REPORT_FILTER_STATE_VERSION),
})
    .strict()
    .superRefine((state, context) => {
    if (state.firstSeenFrom !== null &&
        state.firstSeenTo !== null &&
        state.firstSeenFrom > state.firstSeenTo) {
        context.addIssue({
            code: "custom",
            message: "First-seen date range must not run backwards",
        });
    }
    addOrderedRangeIssue(context, state.durationMin, state.durationMax, "Minimum duration must not exceed maximum duration");
    addOrderedRangeIssue(context, state.spendMin, state.spendMax, "Minimum spend must not exceed maximum spend");
    addOrderedRangeIssue(context, state.roasMin, state.roasMax, "Minimum ROAS must not exceed maximum ROAS");
    const selectedBrands = new Set(state.brandIds);
    for (const [index, selection] of state.groupSelections.entries()) {
        if (!selectedBrands.has(selection.brandId)) {
            context.addIssue({
                code: "custom",
                message: "Subgroup selection is outside the selected brands",
                path: ["groupSelections", index, "brandId"],
            });
        }
    }
    if (state.brandIds.length === 0 && state.accountIds !== null) {
        context.addIssue({
            code: "custom",
            message: "Account filters require at least one selected brand",
            path: ["accountIds"],
        });
    }
    if (encodedReportFilterStateLength(state) > MAXIMUM_REPORT_FILTER_URL_LENGTH) {
        context.addIssue({
            code: "custom",
            message: "Report filter state exceeds the encoded URL length limit",
        });
    }
});
export const reportPlayStatusSchema = z.enum(reportPlayStatuses);
export const filterCatalogAccountSchema = z
    .object({
    id: boundedFlatValue,
    isPresent: z.boolean(),
    name: displayName,
})
    .strict();
export const filterCatalogBrandSchema = z
    .object({
    accounts: z
        .array(filterCatalogAccountSchema)
        .max(MAXIMUM_FILTER_CATALOG_ACCOUNTS)
        .superRefine((accounts, context) => {
        if (new Set(accounts.map(({ id }) => id)).size !== accounts.length) {
            context.addIssue({
                code: "custom",
                message: "Catalog account identifiers must be unique per brand",
            });
        }
    }),
    id: canonicalUuidSchema,
    name: subgroupDisplayName,
})
    .strict();
const filterCatalogBrandsSchema = z
    .object({
    brands: z
        .array(filterCatalogBrandSchema)
        .max(MAXIMUM_REPORT_FILTER_BRANDS)
        .superRefine((brands, context) => {
        if (new Set(brands.map(({ id }) => id)).size !== brands.length) {
            context.addIssue({
                code: "custom",
                message: "Catalog brand identifiers must be unique",
            });
        }
        const accountCount = brands.reduce((total, brand) => total + brand.accounts.length, 0);
        if (accountCount > MAXIMUM_FILTER_CATALOG_ACCOUNTS) {
            context.addIssue({
                code: "custom",
                message: "Filter catalog exceeds the account ceiling",
            });
        }
    }),
})
    .strict();
export const subgroupCatalogValueSchema = z
    .object({
    id: canonicalUuidSchema,
    name: subgroupDisplayName,
    priority: z.number().int().positive(),
})
    .strict();
export const subgroupCatalogGroupSchema = z
    .object({
    brandId: canonicalUuidSchema,
    id: canonicalUuidSchema,
    name: subgroupDisplayName,
    revision: z
        .number()
        .int()
        .min(0)
        .max(MAXIMUM_SUBGROUP_REVISION),
    unassigned: z
        .object({
        id: z.literal(SUBGROUP_UNASSIGNED_VALUE),
        name: z.literal(SUBGROUP_UNASSIGNED_LABEL),
    })
        .strict(),
    values: z
        .array(subgroupCatalogValueSchema)
        .max(MAXIMUM_SUBGROUP_VALUES)
        .superRefine((values, context) => {
        if (new Set(values.map(({ id }) => id)).size !== values.length) {
            context.addIssue({
                code: "custom",
                message: "Subgroup catalog value identifiers must be unique",
            });
        }
    }),
})
    .strict();
export const subgroupCatalogSchema = z
    .array(subgroupCatalogGroupSchema)
    .max(MAXIMUM_SUBGROUPS_PER_BRAND)
    .superRefine((groups, context) => {
    if (new Set(groups.map(({ id }) => id)).size !== groups.length) {
        context.addIssue({
            code: "custom",
            message: "Subgroup catalog group identifiers must be unique",
        });
    }
});
const filterSubgroupCatalogSchema = z
    .object({
    brandId: canonicalUuidSchema,
    groups: subgroupCatalogSchema,
})
    .strict()
    .superRefine(({ brandId, groups }, context) => {
    for (const [index, group] of groups.entries()) {
        if (group.brandId !== brandId) {
            context.addIssue({
                code: "custom",
                message: "Subgroup catalog entries must match their enclosing brand",
                path: ["groups", index, "brandId"],
            });
        }
    }
});
export const filterCatalogSchema = filterCatalogBrandsSchema
    .extend({
    subgroups: z
        .array(filterSubgroupCatalogSchema)
        .max(MAXIMUM_REPORT_FILTER_BRANDS)
        .superRefine((catalogs, context) => {
        if (new Set(catalogs.map(({ brandId }) => brandId)).size !== catalogs.length) {
            context.addIssue({
                code: "custom",
                message: "Filter subgroup catalogs must be unique by brand",
            });
        }
    }),
})
    .strict()
    .superRefine((catalog, context) => {
    if (!sameStringSet(catalog.subgroups.map(({ brandId }) => brandId), catalog.brands.map(({ id }) => id))) {
        context.addIssue({
            code: "custom",
            message: "Filter subgroup catalogs must exactly match catalog brands",
            path: ["subgroups"],
        });
    }
    const subgroupValueCount = catalog.subgroups.reduce((total, { groups }) => total + groups.reduce((brandTotal, group) => brandTotal + group.values.length, 0), 0);
    if (subgroupValueCount > MAXIMUM_FILTER_CATALOG_SUBGROUP_VALUES) {
        context.addIssue({
            code: "custom",
            message: "Filter catalog exceeds the subgroup value ceiling",
            path: ["subgroups"],
        });
    }
});
const savedViewBrandLabelSchema = z
    .object({ id: canonicalUuidSchema, name: subgroupDisplayName })
    .strict();
const savedViewAccountLabelSchema = z
    .object({
    id: boundedFlatValue,
    name: displayName,
})
    .strict();
const savedViewSubgroupLabelSchema = z
    .object({
    brandId: canonicalUuidSchema,
    groupId: canonicalUuidSchema,
    name: subgroupDisplayName,
    values: z
        .array(z
        .object({ id: canonicalUuidSchema, name: subgroupDisplayName })
        .strict())
        .max(MAXIMUM_REPORT_FILTER_VALUES_PER_GROUP)
        .superRefine((values, context) => {
        if (new Set(values.map(({ id }) => id)).size !== values.length) {
            context.addIssue({
                code: "custom",
                message: "Saved subgroup value labels must be unique",
            });
        }
    }),
})
    .strict();
export const savedViewLabelsSchema = z
    .object({
    accounts: z
        .array(savedViewAccountLabelSchema)
        .max(MAXIMUM_REPORT_FILTER_ACCOUNTS)
        .superRefine((accounts, context) => {
        if (new Set(accounts.map(({ id }) => id)).size !== accounts.length) {
            context.addIssue({
                code: "custom",
                message: "Saved account labels must be unique by account",
            });
        }
    }),
    brands: z
        .array(savedViewBrandLabelSchema)
        .max(MAXIMUM_REPORT_FILTER_BRANDS)
        .superRefine((brands, context) => {
        if (new Set(brands.map(({ id }) => id)).size !== brands.length) {
            context.addIssue({
                code: "custom",
                message: "Saved brand labels must be unique",
            });
        }
    }),
    subgroups: z
        .array(savedViewSubgroupLabelSchema)
        .max(MAXIMUM_REPORT_FILTER_GROUP_SELECTIONS)
        .superRefine((groups, context) => {
        const keys = groups.map(({ brandId, groupId }) => `${brandId}\0${groupId}`);
        if (new Set(keys).size !== keys.length) {
            context.addIssue({
                code: "custom",
                message: "Saved subgroup labels must be unique by brand and group",
            });
        }
    }),
})
    .strict();
function sameStringSet(left, right) {
    if (left.length !== right.length)
        return false;
    const expected = new Set(right);
    return left.every((value) => expected.has(value));
}
function validateSavedViewLabelAssociation(state, labels, context) {
    if (!sameStringSet(labels.brands.map(({ id }) => id), state.brandIds)) {
        context.addIssue({
            code: "custom",
            message: "Saved brand labels must exactly match selected brands",
            path: ["labels", "brands"],
        });
    }
    const selectedAccountIds = state.accountIds ?? [];
    if (!sameStringSet(labels.accounts.map(({ id }) => id), selectedAccountIds)) {
        context.addIssue({
            code: "custom",
            message: "Saved account labels must exactly match selected accounts",
            path: ["labels", "accounts"],
        });
    }
    const selections = new Map(state.groupSelections.map((selection) => [
        `${selection.brandId}\0${selection.groupId}`,
        selection,
    ]));
    const labelKeys = labels.subgroups.map(({ brandId, groupId }) => `${brandId}\0${groupId}`);
    if (!sameStringSet(labelKeys, [...selections.keys()])) {
        context.addIssue({
            code: "custom",
            message: "Saved subgroup labels must exactly match selected subgroups",
            path: ["labels", "subgroups"],
        });
        return;
    }
    for (const [index, label] of labels.subgroups.entries()) {
        const selection = selections.get(`${label.brandId}\0${label.groupId}`);
        if (selection &&
            !sameStringSet(label.values.map(({ id }) => id), selection.valueIds)) {
            context.addIssue({
                code: "custom",
                message: "Saved subgroup value labels must exactly match selected values",
                path: ["labels", "subgroups", index, "values"],
            });
        }
    }
}
export const savedViewStorageSchema = z
    .object({
    labels: savedViewLabelsSchema,
    state: reportFilterStateV1Schema,
    version: z.literal(SAVED_VIEW_STORAGE_VERSION),
})
    .strict()
    .superRefine((storage, context) => {
    validateSavedViewLabelAssociation(storage.state, storage.labels, context);
    if (encodedLength(JSON.stringify(storage)) > MAXIMUM_SAVED_VIEW_STORAGE_BYTES) {
        context.addIssue({
            code: "custom",
            message: "Saved view exceeds the storage size limit",
        });
    }
});
const savedViewNameSchema = z
    .string()
    .trim()
    .min(1)
    .max(MAXIMUM_SAVED_VIEW_NAME_LENGTH);
export const savedViewPermissionSchema = z.enum(["VIEW", "EDIT"]);
export const savedViewAccessRoleSchema = z.enum(["OWNER", "VIEW", "EDIT"]);
const savedViewRevisionSchema = z.number().int().min(1).max(2_147_483_647);
const savedViewCollaboratorEmailSchema = z
    .string()
    .trim()
    .min(3)
    .max(320)
    .refine((value) => /^[^\s@]+@[^\s@]+$/u.test(value), "Expected one non-empty email local part and domain");
export const createSavedViewRequestSchema = z
    .object({
    filters: reportFilterStateV1Schema,
    isDefault: z.boolean().optional().default(false),
    name: savedViewNameSchema,
})
    .strict();
export const updateSavedViewRequestSchema = z
    .object({
    expectedRevision: savedViewRevisionSchema.optional(),
    filters: reportFilterStateV1Schema.optional(),
    isDefault: z.boolean().optional(),
    name: savedViewNameSchema.optional(),
})
    .strict()
    .refine(({ filters, isDefault, name }) => filters !== undefined || isDefault !== undefined || name !== undefined, "At least one saved-view field is required")
    .refine(({ expectedRevision, filters, name }) => (filters === undefined && name === undefined) || expectedRevision !== undefined, {
    message: "Content updates require the expected saved-view revision",
    path: ["expectedRevision"],
});
export const addSavedViewCollaboratorRequestSchema = z
    .object({
    email: savedViewCollaboratorEmailSchema,
    permission: savedViewPermissionSchema,
})
    .strict();
export const updateSavedViewCollaboratorRequestSchema = z
    .object({ permission: savedViewPermissionSchema })
    .strict();
export const savedViewCollaboratorSchema = z
    .object({
    createdAt: z.iso.datetime({ offset: true }),
    email: savedViewCollaboratorEmailSchema,
    permission: savedViewPermissionSchema,
    userId: canonicalUuidSchema,
})
    .strict();
export const savedViewCollaboratorListSchema = z
    .array(savedViewCollaboratorSchema)
    .max(MAXIMUM_SAVED_VIEW_COLLABORATORS);
export const savedViewSchema = z
    .object({
    accessRole: savedViewAccessRoleSchema,
    collaboratorCount: z.number().int().min(0).max(MAXIMUM_SAVED_VIEW_COLLABORATORS),
    createdAt: z.iso.datetime({ offset: true }),
    filters: reportFilterStateV1Schema,
    id: canonicalUuidSchema,
    isDefault: z.boolean(),
    isShared: z.boolean(),
    labels: savedViewLabelsSchema,
    name: savedViewNameSchema,
    ownerEmail: savedViewCollaboratorEmailSchema,
    peopleCount: z.number().int().min(1).max(MAXIMUM_SAVED_VIEW_COLLABORATORS + 1),
    revision: savedViewRevisionSchema,
    updatedAt: z.iso.datetime({ offset: true }),
})
    .strict()
    .superRefine((view, context) => {
    validateSavedViewLabelAssociation(view.filters, view.labels, context);
    if (view.peopleCount !== view.collaboratorCount + 1) {
        context.addIssue({
            code: "custom",
            message: "Saved-view people count must include exactly the owner and collaborators",
            path: ["peopleCount"],
        });
    }
    if (view.isShared !== (view.collaboratorCount > 0)) {
        context.addIssue({
            code: "custom",
            message: "Saved-view sharing state must derive from collaborator count",
            path: ["isShared"],
        });
    }
    if (view.isDefault && view.accessRole !== "OWNER") {
        context.addIssue({
            code: "custom",
            message: "Only an actor-owned saved view can be the actor default",
            path: ["isDefault"],
        });
    }
});
export const savedViewListSchema = z
    .array(savedViewSchema)
    .max(MAXIMUM_SAVED_VIEWS_PER_USER);
const reportFilterUrlKeys = [
    "fv",
    "brandIds",
    "accountIds",
    "datePreset",
    "from",
    "to",
    "groupSelections",
    "durationMin",
    "durationMax",
    "firstSeenFrom",
    "firstSeenTo",
    "adNameSearch",
    "objectives",
    "optimizationGoals",
    "playStatuses",
    "roasMin",
    "roasMax",
    "spendMin",
    "spendMax",
];
export class ReportFilterUrlError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = "ReportFilterUrlError";
    }
}
export function encodedSearch(input = "") {
    return (typeof input === "string"
        ? input.startsWith("?")
            ? input.slice(1)
            : input
        : input.toString());
}
function searchParams(input = "") {
    return new URLSearchParams(encodedSearch(input));
}
export function encodedLength(value) {
    return new TextEncoder().encode(value).byteLength;
}
function ownedBracketKey(key) {
    for (const owned of reportFilterUrlKeys) {
        if (key.startsWith(`${owned}[`))
            return owned;
    }
    return null;
}
function invalidUrl(code, message) {
    return { code, kind: "invalid", message };
}
function csv(value) {
    return value === "" ? [""] : value.split(",");
}
function optionalCsv(params, key) {
    const value = params.get(key);
    return value === null ? [] : csv(value);
}
function optionalNumber(params, key) {
    const raw = params.get(key);
    if (raw === null)
        return null;
    if (raw.trim() === "")
        return Number.NaN;
    return Number(raw);
}
export function parseReportFilterUrl(input) {
    if (typeof input !== "string") {
        return invalidUrl("AMBIGUOUS_FILTERS", "Report filters require the raw encoded query string");
    }
    const encoded = encodedSearch(input);
    if (encodedLength(encoded) > MAXIMUM_REPORT_FILTER_URL_LENGTH) {
        return invalidUrl("URL_TOO_LONG", "Report filter URL exceeds the length limit");
    }
    const params = new URLSearchParams(encoded);
    const bracketed = [...params.keys()].find((key) => ownedBracketKey(key));
    const repeated = reportFilterUrlKeys.find((key) => params.getAll(key).length > 1);
    if (bracketed || repeated) {
        return invalidUrl("AMBIGUOUS_FILTERS", "Report filters must use one flat value per key");
    }
    const hasFilter = reportFilterUrlKeys.some((key) => key !== "fv" && params.has(key));
    if (!params.has("fv")) {
        return hasFilter
            ? invalidUrl("AMBIGUOUS_FILTERS", "Explicit report filters require a version marker")
            : { kind: "absent" };
    }
    if (params.get("fv") !== String(REPORT_FILTER_STATE_VERSION)) {
        return invalidUrl("UNSUPPORTED_VERSION", "Unsupported report filter URL version");
    }
    const datePreset = params.get("datePreset");
    const from = params.get("from");
    const to = params.get("to");
    let date;
    if (datePreset !== null) {
        if (from !== null || to !== null) {
            return invalidUrl("AMBIGUOUS_FILTERS", "Preset and custom dates cannot be combined");
        }
        const presetDays = reportDatePresetDays.get(datePreset);
        date = { preset: presetDays ?? datePreset };
    }
    else {
        date = { from, preset: "custom", to };
    }
    let groupSelections = [];
    const encodedGroups = params.get("groupSelections");
    if (encodedGroups !== null) {
        try {
            groupSelections = JSON.parse(encodedGroups);
        }
        catch {
            return invalidUrl("INVALID_FILTERS", "Invalid subgroup selection JSON");
        }
    }
    const parsed = reportFilterStateV1Schema.safeParse({
        accountIds: params.get("accountIds") === null ? null : csv(params.get("accountIds")),
        adNameSearch: params.get("adNameSearch"),
        brandIds: params.get("brandIds") === ""
            ? []
            : params.get("brandIds") === null
                ? undefined
                : csv(params.get("brandIds")),
        date,
        durationMax: optionalNumber(params, "durationMax"),
        durationMin: optionalNumber(params, "durationMin"),
        firstSeenFrom: params.get("firstSeenFrom"),
        firstSeenTo: params.get("firstSeenTo"),
        groupSelections,
        objectives: optionalCsv(params, "objectives"),
        optimizationGoals: optionalCsv(params, "optimizationGoals"),
        playStatuses: optionalCsv(params, "playStatuses"),
        roasMax: optionalNumber(params, "roasMax"),
        roasMin: optionalNumber(params, "roasMin"),
        spendMax: optionalNumber(params, "spendMax"),
        spendMin: optionalNumber(params, "spendMin"),
        version: REPORT_FILTER_STATE_VERSION,
    });
    if (!parsed.success) {
        return invalidUrl("INVALID_FILTERS", parsed.error.issues[0]?.message ?? "Invalid report filters");
    }
    return { kind: "valid", state: parsed.data };
}
function removeOwnedParams(params) {
    for (const key of [...params.keys()]) {
        if (reportFilterUrlKeys.includes(key) ||
            ownedBracketKey(key)) {
            params.delete(key);
        }
    }
}
function appendCommonFilterParams(params, state) {
    params.set("brandIds", state.brandIds.join(","));
    if (state.accountIds !== null) {
        params.set("accountIds", state.accountIds.join(","));
    }
    if (state.groupSelections.length > 0) {
        params.set("groupSelections", JSON.stringify(state.groupSelections));
    }
    if (state.durationMin !== null) {
        params.set("durationMin", String(state.durationMin));
    }
    if (state.durationMax !== null) {
        params.set("durationMax", String(state.durationMax));
    }
    if (state.firstSeenFrom !== null) {
        params.set("firstSeenFrom", state.firstSeenFrom);
    }
    if (state.firstSeenTo !== null) {
        params.set("firstSeenTo", state.firstSeenTo);
    }
    if (state.adNameSearch !== null) {
        params.set("adNameSearch", state.adNameSearch);
    }
    if (state.objectives.length > 0) {
        params.set("objectives", state.objectives.join(","));
    }
    if (state.optimizationGoals.length > 0) {
        params.set("optimizationGoals", state.optimizationGoals.join(","));
    }
    if (state.playStatuses.length > 0) {
        params.set("playStatuses", state.playStatuses.join(","));
    }
    if (state.roasMin !== null) {
        params.set("roasMin", String(state.roasMin));
    }
    if (state.roasMax !== null) {
        params.set("roasMax", String(state.roasMax));
    }
    if (state.spendMin !== null) {
        params.set("spendMin", String(state.spendMin));
    }
    if (state.spendMax !== null) {
        params.set("spendMax", String(state.spendMax));
    }
}
function encodedReportFilterStateLength(state) {
    const params = new URLSearchParams();
    params.set("fv", String(REPORT_FILTER_STATE_VERSION));
    appendCommonFilterParams(params, state);
    if (state.date.preset === "custom") {
        params.set("from", state.date.from);
        params.set("to", state.date.to);
    }
    else {
        params.set("datePreset", String(state.date.preset));
    }
    return encodedLength(params.toString());
}
export function serializeReportFilterUrl(state, existing = "") {
    const parsed = reportFilterStateV1Schema.safeParse(state);
    if (!parsed.success) {
        throw new ReportFilterUrlError("INVALID_FILTERS", parsed.error.issues[0]?.message ?? "Invalid report filter state");
    }
    const params = searchParams(existing);
    removeOwnedParams(params);
    params.set("fv", String(REPORT_FILTER_STATE_VERSION));
    appendCommonFilterParams(params, parsed.data);
    if (parsed.data.date.preset === "custom") {
        params.set("from", parsed.data.date.from);
        params.set("to", parsed.data.date.to);
    }
    else {
        params.set("datePreset", String(parsed.data.date.preset));
    }
    const encoded = params.toString();
    if (encodedLength(encoded) > MAXIMUM_REPORT_FILTER_URL_LENGTH) {
        throw new ReportFilterUrlError("URL_TOO_LONG", "Report filter URL exceeds the length limit");
    }
    return encoded;
}
export function materializeReportFilterDateRange(date, now = new Date()) {
    const parsed = reportFilterDateV1Schema.parse(date);
    if (parsed.preset === "custom") {
        return { from: parsed.from, to: parsed.to };
    }
    if (!Number.isFinite(now.valueOf())) {
        throw new RangeError("A valid current date is required");
    }
    const utcToday = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    const to = utcToday - 86_400_000;
    const from = to - (parsed.preset - 1) * 86_400_000;
    return {
        from: new Date(from).toISOString().slice(0, 10),
        to: new Date(to).toISOString().slice(0, 10),
    };
}
export function materializeReportFilterQuery(state, now = new Date()) {
    const parsed = reportFilterStateV1Schema.parse(state);
    if (parsed.brandIds.length === 0) {
        throw new ReportFilterUrlError("INVALID_FILTERS", "At least one brand is required to run a report query");
    }
    const params = new URLSearchParams();
    appendCommonFilterParams(params, parsed);
    const range = materializeReportFilterDateRange(parsed.date, now);
    params.set("from", range.from);
    params.set("to", range.to);
    if (encodedLength(params.toString()) > MAXIMUM_REPORT_FILTER_URL_LENGTH) {
        throw new ReportFilterUrlError("URL_TOO_LONG", "Report filter URL exceeds the length limit");
    }
    return params;
}
//# sourceMappingURL=report-filters.js.map